import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  Tooltip,
  Legend
);

const RevenueStatsComponent = ({ nav }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState("all");
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const dealerLogin = useSelector((state) => state.dealerLogin);
  const { dealerInfo } = dealerLogin;

  const fetchData = async () => {
    if (dealerInfo && dealerInfo._id) {
      try {
        const params = { dealerId: dealerInfo._id, timePeriod };
        if (timePeriod === "monthly") {
          params.month = month;
          params.year = year;
        } else if (timePeriod === "yearly") {
          params.year = year;
        }
        const response = await axios.get(
          `${process.env.REACT_APP_DP_API}/api/dealer-portal/revenue-stats`,
          {
            params,
            headers: { Authorization: `Bearer ${dealerInfo.token}` },
          }
        );
        setData(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching revenue stats");
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [dealerInfo, timePeriod, month, year]);

  const handleTimePeriodChange = (event) => {
    setTimePeriod(event.target.value);
    setLoading(true);
  };

  const handleMonthChange = (event) => {
    setMonth(event.target.value);
    setLoading(true);
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
    setLoading(true);
  };

  const years = Array.from({ length: 5 }, (_, i) =>
    (new Date().getFullYear() - i).toString()
  );
  const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString());

  if (loading) {
    return (
      <div className="container p-4">
        <div className="d-flex flex-wrap mb-4 gap-4">
          <Skeleton
            variant="rectangular"
            width={200}
            height={40}
            className="rounded"
          />
          <Skeleton
            variant="rectangular"
            width={200}
            height={40}
            className="rounded"
          />
          <Skeleton
            variant="rectangular"
            width={200}
            height={40}
            className="rounded"
          />
        </div>
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
          {Array(4)
            .fill()
            .map((_, index) => (
              <div key={index} className="col">
                <div className="bg-white rounded p-4 shadow">
                  <Skeleton variant="text" width="80%" height={20} className="mb-3" />
                  <Skeleton variant="text" width="50%" height={40} className="mb-2" />
                  <Skeleton
                    variant="rectangular"
                    width={60}
                    height={60}
                    className="rounded"
                  />
                  <Skeleton variant="text" width="70%" height={16} />
                </div>
              </div>
            ))}
        </div>
        <div className="bg-white rounded p-4 shadow mt-4">
          <Skeleton variant="text" width="80%" height={20} className="mb-3" />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            className="rounded"
          />
        </div>
      </div>
    );
  }

  if (error) return <div className="text-danger text-center">{error}</div>;
  if (!data || Object.values(data).every((value) => value === 0 || !value)) {
    return <div className="text-muted text-center">No Revenue Data Available</div>;
  }

  const formatPercentage = (value) => {
    const formattedValue = parseFloat(value).toFixed(2);
    return formattedValue >= 0 ? `+${formattedValue}%` : `${formattedValue}%`;
  };

  const lineChartData = {
    labels: data.revenueTrend.map((item) => item.period),
    datasets: [
      {
        label: "Revenue ($)",
        data: data.revenueTrend.map((item) => item.revenue),
        borderColor: "#007bff",
        backgroundColor: "rgba(0, 123, 255, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  return (
    <div className="container p-4">
      <div className="d-flex flex-wrap mb-4 gap-4">
        <FormControl className="w-48">
          <InputLabel>Time Period</InputLabel>
          <Select
            value={timePeriod}
            onChange={handleTimePeriodChange}
            label="Time Period"
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </Select>
        </FormControl>
        {timePeriod === "monthly" && (
          <>
            <FormControl className="w-48">
              <InputLabel>Month</InputLabel>
              <Select value={month} onChange={handleMonthChange} label="Month">
                {months.map((m) => (
                  <MenuItem key={m} value={m}>
                    {new Date(0, m - 1).toLocaleString("default", {
                      month: "long",
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className="w-48">
              <InputLabel>Year</InputLabel>
              <Select value={year} onChange={handleYearChange} label="Year">
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {timePeriod === "yearly" && (
          <FormControl className="w-48">
            <InputLabel>Year</InputLabel>
            <Select value={year} onChange={handleYearChange} label="Year">
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>
      <h2 className="h2 fw-bold mb-4">Revenue Analytics - {data.periodName}</h2>
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
        <div className="col">
          <div className="bg-white rounded p-4 shadow position-relative overflow-hidden">
            <div className="position-absolute top-0 start-0 end-0 h-1 bg-primary"></div>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="text-muted mb-2">Total Revenue</p>
                <h4 className="h4 fw-bold mb-0">
                  ${data.totalRevenue.toLocaleString()}
                </h4>
              </div>
              <div className="w-16 h-16 rounded bg-primary d-flex align-items-center justify-content-center text-white shadow">
                <i className="bi bi-currency-dollar fs-4"></i>
              </div>
            </div>
            <hr className="my-3 opacity-25" />
            <span className="badge bg-success text-white">
              Total from {data.totalOrders} orders
            </span>
          </div>
        </div>
        <div className="col">
          <div className="bg-white rounded p-4 shadow position-relative overflow-hidden">
            <div className="position-absolute top-0 start-0 end-0 h-1 bg-success"></div>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="text-muted mb-2">Revenue in {data.periodName}</p>
                <h4 className="h4 fw-bold mb-0">
                  ${data.periodRevenue.toLocaleString()}
                </h4>
              </div>
              <div className="w-16 h-16 rounded bg-success d-flex align-items-center justify-content-center text-white shadow">
                <i className="bi bi-calendar-month fs-4"></i>
              </div>
            </div>
            <hr className="my-3 opacity-25" />
            <span
              className={`badge ${
                data.periodRevenueComparison >= 0
                  ? "bg-success text-white"
                  : "bg-danger text-white"
              }`}
            >
              {formatPercentage(data.periodRevenueComparison)}{" "}
              <i
                className={`bi bi-arrow-${
                  data.periodRevenueComparison >= 0 ? "up" : "down"
                }`}
              ></i>{" "}
              Compared to {data.previousPeriodName}
            </span>
          </div>
        </div>
        <div className="col">
          <div className="bg-white rounded p-4 shadow position-relative overflow-hidden">
            <div className="position-absolute top-0 start-0 end-0 h-1 bg-danger"></div>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="text-muted mb-2">Average Order Value</p>
                <h4 className="h4 fw-bold mb-0">
                  ${Math.round(data.averageOrderValue).toLocaleString()}
                </h4>
              </div>
              <div className="w-16 h-16 rounded bg-danger d-flex align-items-center justify-content-center text-white shadow">
                <i className="bi bi-calculator fs-4"></i>
              </div>
            </div>
            <hr className="my-3 opacity-25" />
            <span className="badge bg-success text-white">
              Based on {data.totalOrders} orders
            </span>
          </div>
        </div>
        <div className="col">
          <div className="bg-white rounded p-4 shadow position-relative overflow-hidden">
            <div className="position-absolute top-0 start-0 end-0 h-1 bg-info"></div>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="text-muted mb-2">Revenue by Payment Status</p>
                <h4 className="h5 fw-bold mb-1">
                  Paid: ${data.revenueByPaymentStatus.paid.toLocaleString()}
                </h4>
                <p className="text-muted mb-0">
                  Pending: ${data.revenueByPaymentStatus.pending.toLocaleString()}
                </p>
              </div>
              <div className="w-16 h-16 rounded bg-info d-flex align-items-center justify-content-center text-white shadow">
                <i className="bi bi-piggy-bank fs-4"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded p-4 shadow mt-4">
        <h3 className="h3 fw-bold mb-3">Revenue Trend</h3>
        <div style={{ height: "16rem" }}>
          <Line
            data={lineChartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { position: "top" } },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default RevenueStatsComponent;